import React, { FC, memo, useRef } from 'react';
import { FloatingLabel, FormControl, FormGroup, FormSelect } from 'react-bootstrap';
import { MdClose } from 'react-icons/md';
import EditorWrapper from '../../editor';
import cls from './CreateTariffFormValuePart.module.scss';

type CreateTariffFormValuePartProps = {
  nameValue: string;
  costValue: number;
  labelValue: string | undefined | null;
  nameError: string | undefined;
  typeValue: string;
  teaser: string | undefined;
  teaserError: string | undefined;
  typeError: string | undefined;
  descriptionValue: string | undefined;
  setText: React.Dispatch<React.SetStateAction<string>>;
  onChange: (event: React.ChangeEvent) => void;
  isNew: boolean;
  imageSmallValue?: string;
  imageSmallError?: string;
  imageBigValue?: string;
  imageBigError?: string;
  colorValue?: string;
};

const CreateTariffFormValuePart: FC<CreateTariffFormValuePartProps> = memo((props) => {
  const {
    nameValue,
    nameError,
    onChange,
    costValue,
    isNew,
    typeValue,
    typeError,
    descriptionValue,
    setText,
    labelValue,
    teaser,
    teaserError,
    imageSmallValue,
    imageSmallError,
    imageBigValue,
    imageBigError,
    colorValue,
  } = props;

  const colorInputRef = useRef<HTMLInputElement>(null);
  const currentColor = colorValue || '#EFF2FF';

  const handleSwatchClick = () => {
    colorInputRef.current?.click();
  };

  const handleColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event);
  };

  const handleClear = () => {
    const syntheticEvent = {
      target: {
        name: 'color',
        value: '',
      },
    } as React.ChangeEvent<HTMLInputElement>;
    onChange(syntheticEvent);
  };

  return (
    <div className={cls.TariffData}>
      <h4 className={`title-4 ${cls.Title}`}>Tariff Details</h4>
      <div className="d-flex flex-column gap-3">
        <div className="d-flex justify-content-between gap-3">
          <FormGroup className={`${cls.InputWrapper} w-50`}>
            <FloatingLabel controlId="name" label="Tariff Name*">
              <FormControl
                name="name"
                onChange={onChange}
                value={nameValue}
                isInvalid={!!nameError}
                className={cls.FormField}
              />
            </FloatingLabel>
            {nameError && <span className={cls.Feedback}>{nameError}</span>}
          </FormGroup>
          <FormGroup className={`${cls.InputWrapper} w-25`}>
            <FloatingLabel controlId="cost" label="Price">
              <FormControl
                type="number"
                name="cost"
                onChange={onChange}
                value={isNew ? costValue : (Number(costValue) / 100).toFixed(2)}
                disabled={!isNew}
                className={cls.FormField}
              />
            </FloatingLabel>
          </FormGroup>
          <FormGroup className={`${cls.InputWrapper} w-25`}>
            <FloatingLabel controlId="cost" label="Label">
              <FormControl
                type="text"
                name="label"
                onChange={onChange}
                value={labelValue ?? ''}
                className={cls.FormField}
              />
            </FloatingLabel>
          </FormGroup>
          <FormGroup className={`${cls.InputWrapper} w-25`}>
            <FloatingLabel controlId="type" label="Tariff Type*">
              <FormSelect
                name="type"
                onChange={onChange}
                value={typeValue}
                isInvalid={!!typeError}
                disabled={!isNew}
                className={cls.FormField}
              >
                <option>Select type</option>
                <option value="USER">User</option>
                <option value="SYSTEM">System</option>
              </FormSelect>
            </FloatingLabel>
            {typeError && <span className={cls.Feedback}>{typeError}</span>}
          </FormGroup>
        </div>
        <div className="d-flex gap-3">
          <div className="flex-fill">
            <FloatingLabel controlId="imageSmall" label="Small card image URL (up to 150x125px)">
              <FormControl
                type="text"
                name="imageSmall"
                onChange={onChange}
                value={imageSmallValue || ''}
                isInvalid={!!imageSmallError}
                className={cls.FormField}
              />
              {imageSmallError && <span className={cls.Feedback}>{imageSmallError}</span>}
            </FloatingLabel>
          </div>
          <div className="flex-fill">
            <FloatingLabel controlId="imageBig" label="Large card image URL (up to 292x460px)">
              <FormControl
                type="text"
                name="imageBig"
                onChange={onChange}
                value={imageBigValue || ''}
                isInvalid={!!imageBigError}
                className={cls.FormField}
              />
              {imageBigError && <span className={cls.Feedback}>{imageBigError}</span>}
            </FloatingLabel>
          </div>
          <div className="flex-shrink-0" style={{ position: 'relative' }}>
            <FloatingLabel controlId="color" label="Tariff Color">
              <FormControl
                type="text"
                name="color"
                value={currentColor}
                onChange={handleColorChange}
                className={cls.ColorPickerContainer}
                style={{ padding: 0, background: 'transparent', border: 'none' }}
              />
            </FloatingLabel>
            <div className={cls.ColorPickerContent}>
              <div className={cls.ColorPickerInfo}>
                <p className={cls.ColorPickerHex}>{currentColor.toUpperCase()}</p>
              </div>
              <div
                className={cls.ColorPickerSwatch}
                onClick={handleSwatchClick}
                style={{ backgroundColor: currentColor }}
              >
                <input
                  ref={colorInputRef}
                  type="color"
                  name="color"
                  value={currentColor}
                  onChange={handleColorChange}
                  className={cls.ColorPickerInput}
                />
              </div>
              {colorValue && <MdClose className={cls.ColorPickerClear} onClick={handleClear} />}
            </div>
          </div>
        </div>
        <div>
          <FormGroup className="position-relative">
            <FloatingLabel controlId="teaser" label="Short Preview Description*">
              <FormControl
                type="text"
                name="teaser"
                onChange={onChange}
                value={teaser}
                isInvalid={!!teaserError}
                className={`${cls.FormField} ${cls.FormFieldTeaser}`}
                as="textarea"
              />
            </FloatingLabel>
            {teaserError && <span className={cls.Feedback}>{teaserError}</span>}
          </FormGroup>
        </div>
        <div className="mt-3">
          <h4 className={`title-4 ${cls.Title}`}>Description</h4>
          <EditorWrapper setText={setText} value={descriptionValue ?? ''} />
        </div>
      </div>
    </div>
  );
});

export default CreateTariffFormValuePart;
